import { useEffect, useRef } from 'react';
import { LexicalEditor } from 'lexical';
import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';

import { $generateHtmlFromNodes } from '@lexical/html';

import { captureException } from '@utils/logger';
import { Toolbar } from './plugins/Toolbar';
import { TagsPlugin } from './plugins/TagsPlugin';
import { TagNode } from './nodes/TagNode';
import { HTMLLoaderPlugin } from './plugins/HTMLLoaderPlugin';
import { TagsHelp } from './components/TagsHelp';
import { useGetEmailTags } from './hooks/useGetEmailTags';
import { getContentEditableStyles, getTextEditorInnerStyles, getTextEditorStyles } from './TextEditorStyles';

export type TextEditorProps = {
    initialHTML?: string;
    onChange: (html: string, rootElement: HTMLElement | null) => void;
};

export function TextEditor({ initialHTML, onChange }: TextEditorProps): JSX.Element {
    const availableTags = useGetEmailTags();
    const isInitialised = useRef<boolean>(false);

    useEffect(() => {
        if (initialHTML) {
            isInitialised.current = false;
        }
    }, [initialHTML]);

    const initialConfig: InitialConfigType = {
        namespace: 'TextEditor',
        theme: {
            paragraph: 'text-editor-paragraph',
            text: {
                italic: 'text-editor-text-italic',
                underline: 'text-editor-text-underline',
            },
        },
        onError: (error: Error, editor: LexicalEditor) =>
            captureException(error, { data: editor.getEditorState().toJSON() }),
        nodes: [TagNode],
    };

    return (
        <div css={getTextEditorStyles}>
            <LexicalComposer initialConfig={initialConfig}>
                <Toolbar />

                <div css={getTextEditorInnerStyles}>
                    <RichTextPlugin
                        contentEditable={<ContentEditable css={getContentEditableStyles} />}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                </div>

                <TagsPlugin tags={availableTags} />
                <HistoryPlugin />
                <OnChangePlugin
                    ignoreSelectionChange={true}
                    onChange={(editorState, editor) => {
                        editorState.read(() => {
                            // When loading the initial HTML, we don't want to trigger the onChange callback to control better when the user has modified the content.
                            if (initialHTML && !isInitialised.current) {
                                isInitialised.current = true;
                                return;
                            }

                            const htmlString = $generateHtmlFromNodes(editor);
                            onChange(htmlString, editor.getRootElement());
                        });
                    }}
                />
                <HTMLLoaderPlugin initialHTML={initialHTML} />
            </LexicalComposer>

            <TagsHelp tags={availableTags} />
        </div>
    );
}
